import { replaceColor } from 'lottie-colorify';
import { InteractivityProps, LottieOptions, LottieRef } from 'lottie-react';
import { RendererType } from 'lottie-web';
import dynamic, { LoaderComponent } from 'next/dynamic';
import { AnimationEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { randomFromList } from '../../util/random';

type LottieComponentPropsGeneric = LottieOptions<RendererType> & {
	// Reproduce the LottieComponentProps with support for generic LottieOptions
	interactivity?: Omit<InteractivityProps, 'lottieObj'>;
};

const Lottie = dynamic(() => import('lottie-react').then((module) => module.default) as LoaderComponent<LottieComponentPropsGeneric>, {
	ssr: false,
});

export type RandomLottieAnimationProps = Omit<LottieComponentPropsGeneric, 'animationData'> & {
	/**
	 * List of animations to randomly choose from.
	 */
	animationList: Record<string, unknown>[];
	/**
	 * Control the play state of the animation with a boolean.
	 */
	isPlaying?: boolean;
	/**
	 * Set speed of lottie animation (only read on load, so cannot be changed during runtime).
	 *
	 * @default 1
	 */
	speed?: number;
	/**
	 * Change a color from the lottie animation (using lottie-colorify).
	 */
	modifyColor?: { source: string | number[]; target: string | number[] };
};

/**
 *
 * Component that takes a list of lottie animations and randomly chooses one to play.
 *
 * Built on [lottie-react](https://github.com/Gamote/lottie-react) with a few added abilities:
 *
 * * Play/pause can be controlled by a boolean through the `isPlaying` prop
 * * Playback speed can be set via the `speed` prop
 * * Can change a specific color dynamically from the lottie animation (using [lottie-colorify](https://github.com/xxmuaddib/lottie-colorify)) by passing a `modifyColor` prop
 */
export const RandomLottieAnimation = ({ animationList, autoplay, isPlaying, speed = 1, modifyColor, ...restProps }: RandomLottieAnimationProps) => {
	const ref = useRef() as LottieRef;
	const shouldAutoPlay = typeof isPlaying === 'undefined' ? autoplay : isPlaying && autoplay;

	const randomAnimationPick = useMemo(() => randomFromList(animationList), [animationList]);

	const animationData = useMemo(() => {
		if (!modifyColor) {
			return randomAnimationPick;
		}

		return replaceColor(modifyColor.source, modifyColor.target, randomAnimationPick) as (typeof animationList)[0];
	}, [randomAnimationPick, modifyColor]);

	const updatePlayState = useCallback(() => {
		if (!ref.current) return;

		if (ref.current.setSpeed) {
			ref.current.setSpeed(speed);
		}

		if (ref.current.play) {
			isPlaying ? ref.current.play() : ref.current.pause();
		}
	}, [speed, isPlaying]);

	const handleDOMLoaded = useCallback(
		(e: AnimationEvent<Element>) => {
			updatePlayState();
			if (restProps.onDOMLoaded) {
				restProps.onDOMLoaded(e);
			}
		},
		[updatePlayState, restProps.onDOMLoaded],
	);

	useEffect(() => {
		updatePlayState();
	}, [ref.current, isPlaying, speed]);

	return (
		<Lottie
			animationData={animationData}
			lottieRef={ref}
			autoplay={shouldAutoPlay}
			onDOMLoaded={handleDOMLoaded}
			{...restProps}
		/>
	);
};
